import * as React from 'react'
import * as m from 'motion/react-m'

import * as Progress from '@radix-ui/react-progress'

import { getScoreColors } from '../../util/commentScoreTheme'
import type { ScoreColorVariant } from '../../util/commentScoreTheme'
import { AnimatedNumber } from '../AnimatedNumber'
import { CommentIcon } from './CommentIcon'

import styles from './MiniCommentScore.module.css'

import type { JSX } from 'react'

type MiniCommentScoreProps = {
  score: number
  variant?: ScoreColorVariant
}

export const MiniCommentScore = ({
  score,
  variant = 'neutral',
}: MiniCommentScoreProps): JSX.Element => {
  const colors = getScoreColors(score, variant)
  const gradientId = React.useId()

  return (
    <div className={styles.container}>
      <Progress.Root
        className={styles.root}
        aria-label={`Comment score: ${score}`}
        aria-valuenow={score}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <Progress.Indicator asChild>
          <div className={styles.indicatorContainer}>
            <m.div
              className={styles.inputWrapper}
              initial={{ opacity: 0, x: '-50%', y: '-50%' }}
              animate={{ opacity: 1, x: '-50%', y: '-50%' }}
              transition={{ duration: 0.4, ease: [0.215, 0.61, 0.355, 1] }}
              style={{
                background: `linear-gradient(180deg, ${colors.bg} 0%, transparent 100%)`,
              }}
            >
              <div className={styles.inputPositioner}>
                <div className={styles.input}>
                  <AnimatedNumber
                    value={score}
                    animateAsWhole
                    animateOnStart
                    style={{
                      color: colors.textColor,
                    }}
                  />
                </div>
              </div>
              <m.div
                className={styles.iconWrapper}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{
                  duration: 0.4,
                  delay: 0.2,
                  ease: [0.215, 0.61, 0.355, 1],
                }}
              >
                <CommentIcon
                  size="xsmall"
                  stroke={1.3}
                  className={styles.icon}
                  style={{ color: colors.progressColor }}
                />
              </m.div>
            </m.div>
            <m.svg
              className={styles.indicator}
              viewBox="0 0 100 100"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4, ease: [0.215, 0.61, 0.355, 1] }}
            >
              <defs>
                <linearGradient
                  id={gradientId}
                  x1="0%"
                  y1="0%"
                  x2="0%"
                  y2="100%"
                >
                  <stop
                    offset="0%"
                    stopColor={colors.progressColor}
                    stopOpacity="1"
                  />
                  <stop
                    offset="100%"
                    stopColor={colors.progressColor}
                    stopOpacity="0.1"
                  />
                </linearGradient>
              </defs>
              {/*
               * Progress arc - always full, color changes based on score.
               *
               * Geometry: circle centered at (50, 50) with radius 48 so the
               * arc fills the viewBox edge-to-edge (with the 4-unit stroke
               * reaching from x=0 to x=100). The chord ends at (22, 89) and
               * (78, 89) preserve the original ~70° opening at the bottom
               * for the comment icon.
               */}
              <path
                d="M22 89 A48 48 0 1 1 78 89"
                fill="none"
                stroke={`url(#${gradientId})`}
                strokeWidth="4.8"
                strokeLinecap="round"
              />
            </m.svg>
          </div>
        </Progress.Indicator>
      </Progress.Root>
    </div>
  )
}
